import {AfterViewInit, Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import data from 'data.json'
import moment from 'moment';
import {catchError, debounceTime, EMPTY, fromEvent, map, Subscription, throttleTime} from 'rxjs';
import {BlockDetailSelection, BlockDetailService} from '../../shared/services/block-detail.service';
import {ActivePeriodService} from '../../shared/services/active-period.service';
import {TimelineFilterService} from '../../shared/services/timeline-filter.service';
import {Apollo} from 'apollo-angular';
import {gql} from '../../../gql';
import {BlockCategoryType, TimelineComponent_BlocksQuery} from '../../../gql/graphql';
import {SearchService} from '../../shared/services/search.service';
import {ActivatedRoute} from '@angular/router';

const BLOCK_QUERY = gql(/* GraphQL */ `
  query TimelineComponent_blocks {
    blocks {
      id
      category
      videoUrlCode      
      gathering
      gatheringDate
      downloadFile {
        name
        type
        file {
          filename
          url
        }
        videoUrl
      }
            coverText {
        document(hydrateRelationships: true)
      }
      details {
        document(hydrateRelationships: true)
      }
      dates {
        beginDate
        endDate
        period
        dateDisplayOverride
      }
    }
  }
`);

export type BlockType =
  NonNullable<TimelineComponent_BlocksQuery['blocks']>[number] & {
    beginDate: string;
    endDate: string;
    period: number;
    dateDisplayOverride: string | null;
  };

export type DisplayBlockType = BlockType & { displayId: string }

type BlocksWithinRange = {
  povo: BlockType[],
  po: BlockType[],
  vo: BlockType[]
}

@Component({
  selector: 'app-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent implements OnInit, AfterViewInit {
  @ViewChildren('timelineDay')
  private elementList: QueryList<ElementRef> | null = null;
  @ViewChild('scrollable') private scrollable: ElementRef<HTMLElement> | null = null;

  data = data;
  blocksByDays: [string, DisplayBlockType[]][] = [];
  filteredBlocksByDays: [string, DisplayBlockType[]][] = [];
  indexes: string[] = [];
  offsetIndexes: number[] = [];
  currentIndex = 0;
  subscriptions: Subscription[] = [];
  activeBlock: BlockType | null = null;
  showBlockDetail = false;
  activePeriod = 1;
  periodIndexes: number[] = [];

  public withinRangeOfBlocks: BlocksWithinRange = {
    povo: [],
    vo: [],
    po: [],
  };

  constructor(
    private blockDetailService: BlockDetailService,
    private activePeriodService: ActivePeriodService,
    private timelineFilterService: TimelineFilterService,
    private apollo: Apollo,
    private searchService: SearchService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.searchService.getActiveBlockId().subscribe((output) => {
        this.scrollToActiveBlockIndex(output);
        this.showTimeline();
      }),
      this.apollo
        .watchQuery({
          query: BLOCK_QUERY,
        })
        .valueChanges.pipe(
          catchError((error) => {
            console.error(error);
            return EMPTY;
          }),
          map((output) => {
            return (output.data.blocks ?? []).reduce((acc, block) => {
              const blocks = (block.dates ?? []).map((date, index) => {
                return {
                  ...block,
                  displayId: `${block.id}-${index}`,
                  beginDate: moment(date.beginDate).format('DD-MM-YYYY'),
                  endDate: moment(date.endDate).format('DD-MM-YYYY'),
                  period: date.period ?? 1,
                  dateDisplayOverride: date.dateDisplayOverride ?? null,
                };
              });

              return [...acc, ...blocks];
            }, [] as DisplayBlockType[]);
          }),
        )
        .subscribe((blocks) => {
          this.searchService.setBlocks(blocks);
          const blockDict: { [key: string]: DisplayBlockType[] } = {};

          blocks?.forEach((block) => {
            const displayDate = this.getBlockDisplayDate(block);

            if (blockDict[displayDate.format('DD-MM-YYYY')]) {
              blockDict[displayDate.format('DD-MM-YYYY')].push(block);
            } else {
              blockDict[displayDate.format('DD-MM-YYYY')] = [];
              blockDict[displayDate.format('DD-MM-YYYY')].push(block);
            }
          });

          this.blocksByDays = Object.entries(blockDict).sort(
            ([dateA], [dateB]) =>
              moment(dateA, 'DD-MM-YYYY').isBefore(moment(dateB, 'DD-MM-YYYY'))
                ? -1
                : 1,
          );
          this.blocksByDays.forEach((day) =>
            day[1].sort((a, b) =>
              moment(a.beginDate, 'DD-MM-YYYY').isBefore(
                moment(b.beginDate, 'DD-MM-YYYY'),
              )
                ? -1
                : 1,
            ),
          );
          this.filteredBlocksByDays = [...this.blocksByDays];

          setTimeout(() => {
            this.calculateOffsets();
            this.calculatePeriodIndexes();
            this.calculateIndexes('auto');
          });

          this.blockDetailService
            .getBlockDetailSelection()
            .subscribe((output: BlockDetailSelection) => {
              this.activeBlock = output.block;
              this.showBlockDetail = output.showBlockDetail;
            });

          this.timelineFilterService.getTimelineFilter().subscribe((output) => {
            if (output === BlockCategoryType.POVO) {
              this.filteredBlocksByDays = this.blocksByDays;
            } else {
              this.filteredBlocksByDays = this.blocksByDays.map((day) => [
                day[0],
                day[1].filter(
                  (block) =>
                    block.category === output ||
                    block.category === BlockCategoryType.POVO,
                ),
              ]);
              this.filteredBlocksByDays = this.filteredBlocksByDays.filter(
                (day) => day[1].length !== 0,
              );
            }
            this.searchService.setBlocks(
              this.filteredBlocksByDays.map((day) => day[1]).flat(),
            );
            setTimeout(() => {
              this.calculateOffsets();
              this.calculatePeriodIndexes();
              this.calculateIndexes('smooth');
            });
          });

          this.activePeriodService.getActivePeriod().subscribe((output) => {
            if (!output.navigated) {
              return;
            }
            this.currentIndex = this.filteredBlocksByDays.findIndex(
              (day) => day[1][0].period === output.period,
            );
            this.scrollToIndex('smooth');
          });
          setTimeout(() => {
            this.route.queryParamMap.subscribe((params) => {
              const blockId = params.get('share');
              if (blockId) {
                this.searchService.setActiveBlockId(blockId);
              }
            });
          }, 500);
        }),
    );
  }

  ngAfterViewInit(): void {
    if (this.scrollable) {
      const scrollElement = this.scrollable.nativeElement;

      fromEvent(scrollElement, 'scroll')
        .pipe(throttleTime(200), debounceTime(300))
        .subscribe(() => {
          const closest = this.offsetIndexes.reduce(function (prev, curr) {
            return Math.abs(curr - scrollElement.scrollLeft) <
              Math.abs(prev - scrollElement.scrollLeft)
              ? curr
              : prev;
          });

          this.currentIndex = this.offsetIndexes.indexOf(closest);

          // check if scrolled to the end of the timeline, and return so last block can be viewed
          if (
            scrollElement.clientWidth + scrollElement.scrollLeft ===
            scrollElement.scrollWidth
          ) {
            // this is used to still update the active period
            if (
              this.activePeriod !==
              this.filteredBlocksByDays[this.currentIndex][1][0].period
            ) {
              let newPeriod =
                this.filteredBlocksByDays[this.currentIndex][1][0].period;
              newPeriod = Math.min(newPeriod, this.periodIndexes.length);

              this.activePeriod = newPeriod;
              this.activePeriodService.setActivePeriod(newPeriod, false);
            }
            return;
          }

          // this.scrollToIndex('smooth');
          // needed because scrollToIndex is not triggered anymore
          if (
            this.activePeriod !==
            this.filteredBlocksByDays[this.currentIndex][1][0].period
          ) {
            let newPeriod =
              this.filteredBlocksByDays[this.currentIndex][1][0].period;
            newPeriod = Math.min(newPeriod, this.periodIndexes.length);

            this.activePeriod = newPeriod;
            this.activePeriodService.setActivePeriod(this.activePeriod, false);
          }

          const scrollDate = moment(
            this.indexes[this.currentIndex],
            'DD-MM-YYYY',
          );
          this.withinRangeOfBlocks = this.blocksByDays
            .filter(([day, _]) =>
              moment(day, 'DD-MM-YYYY').isBefore(scrollDate),
            )
            .flatMap(([_, blocks]) => blocks)
            .filter((block: BlockType) =>
              scrollDate.isBetween(
                moment(block.beginDate, 'DD-MM-YYYY'),
                moment(block.endDate, 'DD-MM-YYYY'),
              ),
            )
            .reduce(
              (carry, block) => ({
                povo: [
                  ...carry.povo,
                  ...(block.category === 'POVO' ? [block] : []),
                ],
                po: [...carry.po, ...(block.category === 'PO' ? [block] : [])],
                vo: [...carry.vo, ...(block.category === 'VO' ? [block] : [])],
              }),
              { povo: [], po: [], vo: [] } as BlocksWithinRange,
            );
        });
    }
  }

  calculateIndexes(behavior: string): void {
    this.indexes = [];

    const today: moment.Moment = moment();

    this.filteredBlocksByDays.forEach((day) => {
      this.indexes.push(day[0]);
    });

    let nearestDate = '';
    this.indexes.forEach((date) => {
      const diff = moment(date, 'DD-MM-YYYY').diff(
        moment(today.format('YYYY-MM-DD')),
        'days',
      );
      if (diff >= 0) {
        if (nearestDate) {
          if (
            moment(date, 'DD-MM-YYYY').diff(
              moment(nearestDate, 'DD-MM-YYYY'),
              'days',
            ) < 0
          ) {
            nearestDate = date;
          }
        } else {
          nearestDate = date;
        }
      }
    });
    this.currentIndex = this.indexes.indexOf(nearestDate);

    setTimeout(() => {
      this.scrollToIndex(behavior);
    });
  }

  calculateOffsets(): void {
    this.offsetIndexes = [];
    if (this.elementList) {
      this.elementList.forEach((element) => {
        // @ts-expect-error element.elementRef is not a known property
        this.offsetIndexes.push(element.elementRef.nativeElement.offsetLeft);
      });
    }
  }

  calculatePeriodIndexes(): void {
    this.periodIndexes = [];
    let period = 0;
    this.filteredBlocksByDays.forEach((day, index) => {
      if (day[1][0].period !== period) {
        period = day[1][0].period;
        this.periodIndexes.push(index);
      }
    });
  }

  scrollToIndex(behavior: string): void {
    const scrollEl: HTMLElement | null =
      document.getElementById('day-' + this.indexes[this.currentIndex]) ?? null;
    if (scrollEl !== null) {
      if (behavior === 'smooth') {
        scrollEl.scrollIntoView({
          behavior: 'smooth',
          inline: 'start',
        });
      } else {
        scrollEl.scrollIntoView({
          inline: 'start',
        });
      }
    }

    if (
      this.activePeriod !==
      this.filteredBlocksByDays[this.currentIndex][1][0].period
    ) {
      this.activePeriod =
        this.filteredBlocksByDays[this.currentIndex][1][0].period;
      this.activePeriodService.setActivePeriod(this.activePeriod, false);
    }
  }

  scrollToActiveBlockIndex(id: string): void {
    this.filteredBlocksByDays.forEach((day) => {
      day[1].forEach((block) => {
        if (block.displayId === id) {
          this.currentIndex = this.filteredBlocksByDays.indexOf(day);
          this.scrollToIndex('smooth');
        }
      });
    });
  }

  scrollToNextItem() {
    this.currentIndex++;
    if (this.currentIndex >= this.indexes.length) {
      this.currentIndex = this.indexes.length - 1;
    }
    this.scrollToIndex('smooth');
  }

  scrollToPreviousItem() {
    this.currentIndex--;
    if (this.currentIndex < 0) {
      this.currentIndex = 0;
      return;
    }
    this.scrollToIndex('smooth');
  }

  showTimeline() {
    if (this.activeBlock) {
      this.blockDetailService.setBlockDetailSelection(this.activeBlock, false);
    }
  }

  public scrollToBlock(block: BlockType) {
    const blockDisplayDate = this.getBlockDisplayDate(block);
    const index = this.getIndexByDate(blockDisplayDate.format('DD-MM-YYYY'));

    if (index === -1) {
      return;
    }

    this.setCurrentIndex(index);
  }

  private getIndexByDate(date: string): number {
    return this.indexes.indexOf(date);
  }

  private setCurrentIndex(index: number): void {
    this.currentIndex = index;
    this.scrollToIndex('smooth');
  }

  private getBlockDisplayDate(block: BlockType): moment.Moment {
    const today: moment.Moment = moment();
    const beginDate: moment.Moment = moment(block.beginDate, 'DD-MM-YYYY');
    const endDate: moment.Moment = moment(block.endDate, 'DD-MM-YYYY');
    let displayDate = beginDate;

    if (
      beginDate.isBefore(today) &&
      !moment(endDate).isBefore(moment(), 'day')
    ) {
      displayDate = today;
    }

    return displayDate;
  }
}
